import React, { useEffect, useState } from "react";
import { X } from "lucide-react";
import Seo from "@/components/Seo";
import PageHero from "@/components/PageHero";
import { Reveal } from "@/components/Reveal";
import BeforeAfterSlider from "@/components/BeforeAfterSlider";
import SectionCTA from "@/components/SectionCTA";
import api, { API } from "@/lib/api";
import { GALLERY_CATS, IMG } from "@/data/site";

const fileUrl = (id) => `${API}/gallery/file/${id}`;

export default function Gallery() {
  const [active, setActive] = useState("All");
  const [uploads, setUploads] = useState([]);
  const [lightbox, setLightbox] = useState(null);

  useEffect(() => {
    api.get("/gallery").then((r) => setUploads(r.data || [])).catch(() => setUploads([]));
  }, []);

  const items = uploads.map((u) => ({ ...u, src: fileUrl(u.id) }));

  // Build Before/After transformation pairs (grouped by pair label).
  const tagged = items.filter((i) => i.tag === "before" || i.tag === "after");
  const groups = {};
  tagged.forEach((i) => {
    const key = (i.pair || i.caption || i.id).toLowerCase().trim();
    groups[key] = groups[key] || {};
    groups[key][i.tag] = i;
  });
  const pairs = Object.entries(groups).filter(([, g]) => g.before && g.after);

  const filtered = active === "All" ? items : items.filter((i) => i.cat === active || i.category === active);

  const showPairs = active === "Before & After";

  return (
    <div data-testid="gallery-page">
      <Seo title="Gallery" description="A gallery of tiling, bathroom, wet room, kitchen splashback, floor and feature wall projects by Rossi Ceramics." />
      <PageHero eyebrow="Our work" title="A gallery of transformations" subtitle="Browse real finishes by category — bathrooms, wet rooms, splashbacks, floors, feature walls and true before & after pairs." image={IMG.feature[0]} />

      <section className="container-rossi py-16">
        <div className="flex flex-wrap gap-2 mb-10">
          {GALLERY_CATS.map((c) => (
            <button
              key={c}
              data-testid={`gallery-cat-${c.toLowerCase().replace(/\s+|&/g, "-")}`}
              onClick={() => setActive(c)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${active === c ? "bg-rossi-gold text-rossi-bg" : "border border-rossi-gold/30 text-rossi-cream/80 hover:bg-rossi-gold/10"}`}
            >
              {c}
            </button>
          ))}
        </div>

        {showPairs ? (
          pairs.length ? (
            <div className="grid md:grid-cols-2 gap-8" data-testid="gallery-pairs">
              {pairs.map(([label, g]) => (
                <Reveal key={label}>
                  <div className="card-lux p-3">
                    <BeforeAfterSlider before={fileUrl(g.before.id)} after={fileUrl(g.after.id)} height="h-[340px]" />
                    <p className="text-center text-sm text-rossi-cream/80 mt-3">{g.after.caption || g.before.caption || "Transformation"}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          ) : (
            <p className="text-center text-rossi-stone py-16">No before &amp; after pairs yet. Staff can tag two photos (a Before and an After) with the same pair label in the portal to create one.</p>
          )
        ) : (
          <>
            <div className="columns-1 sm:columns-2 lg:columns-3 gap-6 [&>*]:mb-6">
              {filtered.map((item, i) => (
                <Reveal key={item.id} delay={(i % 6) * 0.03}>
                  <button onClick={() => setLightbox(item)} className="block w-full group relative overflow-hidden rounded-2xl border border-rossi-gold/15" data-testid={`gallery-item-${i}`}>
                    <img src={item.src} alt={item.caption || `${item.category} project by Rossi Ceramics`} loading="lazy" className="w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                    <div className="absolute inset-0 bg-gradient-to-t from-rossi-bg/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end p-4">
                      <span className="text-xs font-mono uppercase tracking-widest text-rossi-gold">{item.category}{item.tag ? ` · ${item.tag}` : ""}</span>
                    </div>
                  </button>
                </Reveal>
              ))}
            </div>
            {filtered.length === 0 && <p className="text-center text-rossi-stone py-16">No images in this category yet.</p>}
          </>
        )}
      </section>

      {lightbox && (
        <div className="fixed inset-0 z-[60] bg-rossi-bg/95 flex items-center justify-center p-4" onClick={() => setLightbox(null)} data-testid="gallery-lightbox">
          <button className="absolute top-6 right-6 text-rossi-cream hover:text-rossi-gold" onClick={() => setLightbox(null)}><X size={30} /></button>
          <div className="max-w-5xl w-full" onClick={(e) => e.stopPropagation()}>
            <img src={lightbox.src} alt={lightbox.caption || lightbox.category} className="w-full max-h-[80vh] object-contain rounded-xl" />
            <p className="text-center text-rossi-cream/80 mt-4">{lightbox.caption || lightbox.category}</p>
          </div>
        </div>
      )}

      <SectionCTA />
    </div>
  );
}
